import React, { useState, useRef, useEffect, Fragment } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from '@material-ui/core/styles';
import { useFormik } from 'formik';
import { url } from "../../reducers/url";
import { useSelector } from "react-redux";
import PubSub from 'pubsub-js';
import { post } from '../../helpers/fetchServicesMethods';
import { set as setInStorage, STORAGE_DOMAINS } from '../../services/storage/GQStorage';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import CancelIcon from '@material-ui/icons/Cancel';

const dbs = 'cat:GQ_PATENT';

function showLoader() {
    PubSub.publish('msg', true);
}

function hideLoader() {
    PubSub.publish('msg', false);
}

async function getPatentDetails(queryID, history) {
    try {
        showLoader();
        const response = await post(url.kwSearchResult, { q: queryID, field: "PN" }, history)
            .catch((error) => {
                console.log("error::", error);
            });
        hideLoader();
        return response;
    } catch (error) {
        hideLoader();
        console.error(error);
    }
}

const useStyles = makeStyles((theme) => ({
    grow: {
        flexGrow: 1,
        width: '96%',
    },
    tableLayout: {
        width: '96%',
        boxSizing: 'border-box',
        margin: '0px',
        borderRight: '1px solid #dcd7c0',
        borderLeft: '1px solid #dcd7c0',
        borderBottom: '1px solid #dcd7c0',
    },
    textAreaCss: {
        padding: '0 23px',
    },
    fontDetails: {
        fontFamily: 'verdana, helvetica',
        fontSize: '12px'
    },
}));

const sectionHeadingStyle = {
    scrollMarginTop: '90px',
    color: '#1f78b4',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    fontSize: '15px',
    fontFamily: 'verdana, helvetica',
};

function NewPatentNumber() {
    const classes = useStyles();
    const history = useHistory();
    const [textareaError, setTextareaError] = useState('');
    const fileInputRef = useRef(null);
    const toggle = useSelector(state => state.setToggle.toggle);
    const formik = useFormik({
        initialValues: {
            textareaId: '',
        },
    });

    const [pnResult, setPnResult] = useState(null);
    const [qmap, setQmap] = useState([]);
    const [qmapKeys, setQmapKeys] = useState([]);
    const [primaryDbType, setPrimaryDbType] = useState('all');
    const [downloadWarning, setDownloadWarning] = useState({ open: false, url: '' });

    useEffect(() => {
        const unlisten = history.listen(() => {
            if (fileInputRef.current) {
                fileInputRef.current.value = null;
            }
        });
        return () => {
            unlisten();
        };
    }, [history]);

    const handleSearch = async () => {
        const tokens = formik.values.textareaId.split(/[\s,;]+/).filter(t => t.trim() !== "");
        const patentPattern = /^[A-Z]{2,4}\d+[A-Z0-9]*$/i;
        const invalidTokens = tokens.filter(t => !patentPattern.test(t.trim()));
        if (invalidTokens.length > 0) {
            setTextareaError("Invalid patent number(s): " + invalidTokens.join(", "));
            return;
        }
        setTextareaError("");
        if (!formik.values.textareaId) return;

        const queryID = formik.values.textareaId;
        const result = await getPatentDetails(queryID, history);
        setPnResult(result);

        if (result && result.response_content) {
            const qmapList = result.response_content.qmap
                ? Object.entries(result.response_content.qmap).map(([key, value]) => ({ key, value }))
                : [];
            setQmap(qmapList);
            setPrimaryDbType(result.response_content.primary_db_type || 'all');
            const keys = qmapList.map(item => item.key);
            setQmapKeys(keys);
            setInStorage('queryID', queryID, STORAGE_DOMAINS.SESSION);
            setInStorage('qmapKeys', keys, STORAGE_DOMAINS.SESSION);
        }
    };

    function getPatentStatus(value) {
        if (value.found === 'MISS') {
            return { variant: 'miss', tooltip: 'not found', link: false };
        }
        if (value.found === 'INDIRECT') {
            return { variant: 'warn', tooltip: 'found only in family', link: false };
        }
        const foundNuc = !!value.found_nuc;
        const foundPro = !!value.found_pro;
        const hasTypeFlags = foundNuc || foundPro;
        if (hasTypeFlags && primaryDbType === 'nuc' && !foundNuc && foundPro) {
            return { variant: 'warn', tooltip: 'This patent is not found in NUC DB but in PROT DB', link: true };
        }
        if (hasTypeFlags && primaryDbType === 'pro' && !foundPro && foundNuc) {
            return { variant: 'warn', tooltip: 'This patent is not found in PROT DB but in NUC DB', link: true };
        }
        return { variant: 'found', tooltip: 'found', link: true };
    }

    function renderStatusIcon(status) {
        if (status.variant === 'found') {
            return <CheckCircleIcon style={{ color: 'green' }} />;
        }
        if (status.variant === 'miss') {
            return <CancelIcon style={{ color: '#d44848' }} />;
        }
        return <WarningIcon style={{ color: '#e9b34f' }} />;
    }

    function renderPatentRow(item, index) {
        const status = getPatentStatus(item.value);
        const inputNote = item.key !== item.value.original ? `(input PN: ${item.value.original})` : '';
        return (
            <tr className="table_body" key={index}>
                <td style={{ padding: '4px' }}>
                    <Tooltip title={status.tooltip}>{renderStatusIcon(status)}</Tooltip>
                </td>
                <td>
                    {status.link ? (
                        <a
                            href={`${process.env.REACT_APP_API_URL}${url.patentNumberOldLink.replace('**', encodeURIComponent(item.key)).replace('##', dbs)}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {item.key}
                        </a>
                    ) : item.key}
                    {inputNote}
                </td>
                {item.value.found === 'INDIRECT' && <td colSpan="3" style={{ padding: '4px' }}>This patent is not directly found, but it is found in the family data &nbsp;
                    <a href={process.env.REACT_APP_API_URL + url.matchingPNorRF.replace('**', encodeURIComponent(item.key)).replace('$$', encodeURIComponent(item.key)).replace('##', dbs)} target="_blank" rel="noreferrer">
                        See its related patents
                    </a></td>}
                {item.value.found === 'MISS' && <td style={{ padding: '4px' }}></td>}
                {item.value.found === 'DIRECT' &&
                    <td>
                        <a href={process.env.REACT_APP_API_URL + url.pnFamilyId.replace('**', encodeURIComponent(item.value.pb)).replace('##', dbs)} target="_blank" rel="noreferrer">
                            {item.value.pb}</a>
                    </td>
                }
                {item.value.found === 'DIRECT' &&
                    <td style={{ padding: '4px' }}>{item.value.count}&nbsp;(
                        {item.value.count > 20000 ? (
                            <a
                                href={process.env.REACT_APP_API_URL + url.pnSeqListing.replace('**', encodeURIComponent(item.key)).replace('##', dbs)}
                                title="See the sequence listing"
                                rel="noreferrer"
                                onClick={(e) => { e.preventDefault(); setDownloadWarning({ open: true, url: process.env.REACT_APP_API_URL + url.pnSeqListing.replace('**', encodeURIComponent(item.key)).replace('##', dbs) }); }}
                            >
                                Download
                            </a>
                        ) : (
                            <a href={process.env.REACT_APP_API_URL + url.pnSeqListing.replace('**', encodeURIComponent(item.key)).replace('##', dbs)} target="_blank" title="See the sequence listing" rel="noreferrer">
                                Download
                            </a>
                        )})
                    </td>}
                {item.value.found !== 'DIRECT' && <td style={{ padding: '4px' }}></td>}
                <td style={{ padding: '4px' }}>{item.value.found === 'DIRECT' && <a target="_blank" href={"#/fulldocview/" + item.key + (item.value.kc || '')} rel="noreferrer">Full Text Document</a>}</td>
            </tr>
        );
    }

    function renderPatentSection(title, items, id) {
        if (!items || items.length === 0) {
            return null;
        }
        return (
            <Fragment>
                <br />
                <h6 id={id} align='left' style={sectionHeadingStyle}>{title}</h6>
                <table style={{ width: '100%', tableLayout: 'fixed', fontFamily: 'verdana, helvetica', fontSize: '12px', border: '1px solid #dcd7c0' }}>
                    <thead className='resulttable_head'>
                        <tr>
                            <th style={{ width: '5%', padding: '4px', fontWeight: 'normal' }}></th>
                            <th style={{ width: '30%', borderLeft: '1px solid white', fontWeight: 'normal' }}>Patent Number</th>
                            <th style={{ width: '20%', borderLeft: '1px solid white', fontWeight: 'normal' }}>Family Id</th>
                            <th style={{ width: '20%', borderLeft: '1px solid white', fontWeight: 'normal' }}>Number of SEQ</th>
                            <th style={{ width: '25%', borderLeft: '1px solid white', fontWeight: 'normal' }}>Full Text</th>
                        </tr>
                    </thead>
                    <tbody className='tableCSS' style={{ verticalAlign: 'top' }}>
                        {items.map((item, index) => renderPatentRow(item, index))}
                    </tbody>
                </table>
            </Fragment>
        );
    }

    async function matchingResultTab(filter, pn) {
        const workflowUrl = process.env.REACT_APP_API_URL + 'do=gqfetch&db=' + dbs + '&masterview=&';
        const data = { filter };
        const fullUrl = `${workflowUrl}${new URLSearchParams(data).toString()}`;

        if (fullUrl.length > 2070) {
            const sessionizeUrl = process.env.REACT_APP_API_URL + "do=gqfetch.sessionize&db=" + dbs + '&masterview=';
            const filterUrl = process.env.REACT_APP_API_URL + "do=gqfetch&filter=session&db=" + dbs + '&masterview=';
            try {
                const sessionizeResponse = await fetch(sessionizeUrl, {
                    method: 'POST',
                    body: JSON.stringify(data),
                    headers: { 'Content-Type': 'application/json' },
                });
                if (sessionizeResponse && sessionizeResponse.ok) {
                    window.open(filterUrl, '_blank');
                }
            } catch (error) {
                console.error('Error making the sessionize request:', error);
            }
        } else {
            try {
                let smallPnUrl;
                if (pn) {
                    smallPnUrl = process.env.REACT_APP_API_URL + url.matchingPN.replace('**', encodeURIComponent(qmapKeys)).replace('##', dbs);
                } else {
                    smallPnUrl = process.env.REACT_APP_API_URL + url.matchingPNorRF.replace('**', encodeURIComponent(qmapKeys)).replace('$$', encodeURIComponent(qmapKeys)).replace('##', dbs);
                }
                window.open(smallPnUrl, '_blank');
            } catch (error) {
                console.error('Error making the GET request:', error);
            }
        }
    }

    function handleEmailClick(id) {
        const subject = encodeURIComponent('GQ Error [' + id + ']');
        window.location.href = `mailto:GQ-Support@aptean.com?subject=${subject}`;
    }

    function scrollToSection(id) {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    const isSearchDisabled = !formik.values.textareaId || formik.values.textareaId.trim() === '';

    const foundPatents = qmap.filter(item => item.value.found === 'DIRECT' || item.value.found === 'INDIRECT');
    const notFoundPatents = qmap.filter(item => item.value.found === 'MISS');

    const summaryTiles = [
        { id: 'section-nuc', title: 'Patents Found in GQPAT', count: foundPatents.length },
        { id: 'section-not-found', title: 'Patent Not Found', count: notFoundPatents.length },
    ];

    return (
        <div className="grow">
            <div className={classes.tableLayout}>
                <form>
                    <div>
                        <div style={{ padding: '5px' }}>
                            <br></br>
                            <div className={classes.textAreaCss}>
                                {toggle ?
                                    <textarea
                                        id="textareaId"
                                        name="textareaId"
                                        style={{ border: '1px solid #dcd7c0', width: '100%', boxSizing: 'border-box', height: '150px', outline: 'none', fontFamily: 'verdana, helvetica', fontSize: '14px' }}
                                        value={formik.values.textareaId}
                                        onChange={(e) => { setTextareaError(''); formik.handleChange(e); }}
                                    ></textarea>
                                    :
                                    <textarea
                                        id="textareaId"
                                        name="textareaId"
                                        style={{ border: '1px solid #dcd7c0', width: '100%', boxSizing: 'border-box', height: '150px', outline: 'none', fontFamily: 'verdana, helvetica', fontSize: '14px', backgroundColor: '#292929', color: 'white' }}
                                        value={formik.values.textareaId}
                                        onChange={(e) => { setTextareaError(''); formik.handleChange(e); }}
                                    ></textarea>
                                }
                                {textareaError && (
                                    <span style={{ color: 'red', fontSize: '12px', marginLeft: '4px' }}>
                                        {textareaError}
                                    </span>
                                )}
                                <br></br>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginTop: '4px' }}>
                                    <span className="pnTextData">
                                        For example: WO2010151773
                                            US20080038787,
                                            EP1721609,
                                            WO200209645,
                                            AU9052001, EP1073751, US707495
                                    </span>
                                    <Button
                                        variant="contained"
                                        disableRipple={true}
                                        className={"ftSearchButton " + (isSearchDisabled ? 'submitDisabled' : 'loginSubmitButton')}
                                        disabled={isSearchDisabled}
                                        onClick={handleSearch}
                                        style={toggle ? { padding: '0px 37px' } : { background: '#736d6d', padding: '0px 37px' }}
                                    >
                                        Search
                                    </Button>
                                </div>
                            </div>
                            <br></br>
                        </div>
                    </div>
                </form>

                {pnResult && pnResult.response_status === 0 && (
                    <div align='center' style={{ fontFamily: 'verdana, helvetica', fontSize: '12px' }}>
                        <div style={{ paddingTop: '14px' }}>
                            See all sequences with either&nbsp;
                            <button style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#337ab7', textDecoration: 'underline', font: 'inherit' }} onClick={() => {
                                const encodedFilter = `(filters:!((O:CT,P:SEQUENCE_PN,V:'${qmapKeys}')),matchtype:AND,ver:2)`;
                                matchingResultTab(encodedFilter, true);
                            }}><b>matching patent number</b></button>
                            &nbsp;or&nbsp;
                            <button style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#337ab7', textDecoration: 'underline', font: 'inherit' }} onClick={() => {
                                const encodedFilter = `(filters:!((O:CT,P:SEQUENCE_PN,V:'${qmapKeys}'),(O:CT,P:SEQUENCE_PM,V:'${qmapKeys}')),matchtype:OR,ver:2)`;
                                matchingResultTab(encodedFilter, false);
                            }}><b> matching patent number or in related families</b></button>
                        </div>
                        <br />
                        <div style={{ display: 'flex', gap: '12px', margin: '4px 0 18px 0', flexWrap: 'wrap' }}>
                            {summaryTiles.map(tile => (
                                <div
                                    key={tile.id}
                                    onClick={() => scrollToSection(tile.id)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') scrollToSection(tile.id); }}
                                    style={{
                                        flex: '1 1 0',
                                        minWidth: '120px',
                                        background: '#1f78b4',
                                        color: '#ffffff',
                                        borderRadius: '6px',
                                        padding: '12px 8px',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                    }}
                                >
                                    <div style={{ fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: '1.3' }}>
                                        {tile.title}
                                    </div>
                                    <div style={{ fontSize: '24px', fontWeight: 'bold', marginTop: '8px' }}>
                                        {tile.count}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div>
                            {renderPatentSection('Patents Found in GQPAT', foundPatents, 'section-nuc')}
                            {renderPatentSection('Patent Not Found', notFoundPatents, 'section-not-found')}
                        </div>
                    </div>
                )}

                {pnResult && pnResult.response_status === 1 && (
                    pnResult.response_content.type === 'GQSCRIPTKEYWORDSYNTAXEXCEPTION' ? (
                        <div style={{ padding: '12px' }}>
                            <p style={{ color: 'black' }}>{pnResult.response_content.message}</p>
                            <button style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#337ab7', textDecoration: 'underline', font: 'inherit' }} onClick={() => history.goBack()}>Click here to return to the previous page</button>
                        </div>
                    ) : (
                        <div style={{ padding: '12px' }}>
                            <p style={{ color: 'black', fontFamily: 'verdana, helvetica', fontSize: '12px' }}>{pnResult.response_content.message}</p>
                            <button style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#337ab7', textDecoration: 'underline', fontFamily: 'verdana, helvetica', fontSize: '12px' }} onClick={() => handleEmailClick(pnResult.response_content.id)}>
                                E-mail this Error to GenomeQuest Support</button>
                            <br />
                            <br />
                            <button style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#337ab7', textDecoration: 'underline', fontFamily: 'verdana, helvetica', fontSize: '12px' }} onClick={() => history.goBack()}>
                                Click here to return to the previous page
                            </button>
                        </div>
                    )
                )}
            </div>

            {downloadWarning.open && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        background: toggle ? '#fff' : '#292929',
                        color: toggle ? '#000' : 'white',
                        border: '1px solid #dcd7c0',
                        borderRadius: '6px', padding: '24px 28px', maxWidth: '420px',
                        width: '90%', boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                        fontFamily: 'verdana, helvetica', fontSize: '13px',
                    }}>
                        <p style={{ margin: '0 0 18px 0', lineHeight: '1.5' }}>
                            This patent has more than 20,000 sequences. Only the first <strong>20,000</strong> sequences will be downloaded.
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                className="gqblue"
                                style={{ padding: '5px 15px', color: 'white', borderRadius: '4px', cursor: 'pointer' }}
                                onClick={() => {
                                    window.open(downloadWarning.url, '_blank');
                                    setDownloadWarning({ open: false, url: '' });
                                }}
                            >
                                OK
                            </button>
                            <button
                                style={{ padding: '5px 15px', borderRadius: '4px', cursor: 'pointer', border: '1px solid #dcd7c0', background: toggle ? '#f5f5f5' : '#3a3a3a', color: toggle ? '#000' : 'white' }}
                                onClick={() => setDownloadWarning({ open: false, url: '' })}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default NewPatentNumber;
